import { useState } from 'react';
import { Form } from 'react-bootstrap';
import { TextFormatButtons, TokenInsertButtons, appendToken } from './tokenTools';
import IconClassPicker from './IconClassPicker';

export default function TypographyEditor({ type, content, update }) {
  const [focusedField, setFocusedField] = useState('text');

  const insertToken = (token) => {
    if (type === 'quote' && focusedField === 'author') {
      update('author', appendToken(content.author || '', token));
      return;
    }

    if (type === 'html') {
      update('html', appendToken(content.html || '', token));
      return;
    }

    update('text', appendToken(content.text || '', token));
  };

  switch (type) {

    case 'heading':
      return (
        <div className="pagebuilder-editor-fields">
          <TextFormatButtons />
          <Form.Group className="mb-3">
            <Form.Label>Level</Form.Label>
            <Form.Select value={content.level || 'h2'} onChange={(e) => update('level', e.target.value)}>
              <option value="h1">H1 — Page Title</option>
              <option value="h2">H2 — Section Title</option>
              <option value="h3">H3 — Sub-section</option>
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label>Text</Form.Label>
            <Form.Control
              value={content.text || ''}
              onChange={(e) => update('text', e.target.value)}
              onFocus={() => setFocusedField('text')}
              placeholder="Heading text…"
            />
          </Form.Group>
          <IconClassPicker
            value={content.icon || ''}
            onChange={(nextIconClass) => update('icon', nextIconClass)}
            maxRows={2}
          />
          <TokenInsertButtons onInsert={insertToken} />
        </div>
      );

    case 'paragraph':
      return (
        <div className="pagebuilder-editor-fields">
          <TextFormatButtons />
          <Form.Group>
            <Form.Label>Text</Form.Label>
            <Form.Control
              as="textarea"
              rows={5}
              value={content.text || ''}
              onChange={(e) => update('text', e.target.value)}
              onFocus={() => setFocusedField('text')}
              placeholder="Paragraph text…"
            />
          </Form.Group>
          <TokenInsertButtons onInsert={insertToken} />
        </div>
      );

    case 'html':
      return (
        <div className="pagebuilder-editor-fields">
          <TextFormatButtons />
          <Form.Group>
            <Form.Label>HTML</Form.Label>
            <Form.Control
              as="textarea"
              rows={7}
              value={content.html || ''}
              onChange={(e) => update('html', e.target.value)}
              onFocus={() => setFocusedField('html')}
              placeholder="<div>Your custom HTML</div>"
              style={{ fontFamily: 'monospace', fontSize: '0.88rem' }}
            />
            <Form.Text className="pagebuilder-help-note">
              Supports raw HTML. Use with care and only trusted content.
            </Form.Text>
          </Form.Group>
          <TokenInsertButtons onInsert={insertToken} />
        </div>
      );

    case 'quote':
      return (
        <div className="pagebuilder-editor-fields">
          <TextFormatButtons />
          <Form.Group className="mb-3">
            <Form.Label>Quote text</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={content.text || ''}
              onChange={(e) => update('text', e.target.value)}
              onFocus={() => setFocusedField('text')}
              placeholder="The quote…"
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Author</Form.Label>
            <Form.Control
              value={content.author || ''}
              onChange={(e) => update('author', e.target.value)}
              onFocus={() => setFocusedField('author')}
              placeholder="Author name…"
            />
          </Form.Group>
          <IconClassPicker
            value={content.icon || ''}
            onChange={(nextIconClass) => update('icon', nextIconClass)}
            maxRows={2}
          />
          <TokenInsertButtons onInsert={insertToken} />
        </div>
      );

    case 'callout':
      return (
        <div className="pagebuilder-editor-fields">
          <TextFormatButtons />
          <div className="pagebuilder-field-row mb-3">
            <Form.Group style={{ flex: '0 0 160px' }}>
              <Form.Label>Variant</Form.Label>
              <Form.Select value={content.variant || 'info'} onChange={(e) => update('variant', e.target.value)}>
                <option value="info">ℹ️ Info</option>
                <option value="warning">⚠️ Warning</option>
                <option value="success">✅ Success</option>
                <option value="danger">🚨 Danger</option>
              </Form.Select>
            </Form.Group>
          </div>
          <IconClassPicker
            value={content.icon || ''}
            onChange={(nextIconClass) => update('icon', nextIconClass)}
            maxRows={2}
          />
          <Form.Group>
            <Form.Label>Message</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={content.text || ''}
              onChange={(e) => update('text', e.target.value)}
              onFocus={() => setFocusedField('text')}
              placeholder="Callout message…"
            />
          </Form.Group>
          <TokenInsertButtons onInsert={insertToken} />
        </div>
      );

    case 'highlightBox':
      return (
        <div className="pagebuilder-editor-fields">
          <TextFormatButtons />
          <Form.Group className="mb-3">
            <Form.Label>Color</Form.Label>
            <Form.Select value={content.color || 'yellow'} onChange={(e) => update('color', e.target.value)}>
              <option value="yellow">Yellow</option>
              <option value="blue">Blue</option>
              <option value="green">Green</option>
              <option value="pink">Pink</option>
              <option value="gray">Gray</option>
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label>Text</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={content.text || ''}
              onChange={(e) => update('text', e.target.value)}
              onFocus={() => setFocusedField('text')}
              placeholder="Highlighted text…"
            />
          </Form.Group>
          <TokenInsertButtons onInsert={insertToken} />
        </div>
      );

    case 'badge': {
      const items = content.items || [];
      const addItem = () => update('items', [...items, { id: `badge-${Date.now()}`, text: '', variant: 'primary' }]);
      const removeItem = (iid) => update('items', items.filter((item) => item.id !== iid));
      const updateItem = (iid, field, val) => update('items', items.map((item) => item.id === iid ? { ...item, [field]: val } : item));
      return (
        <div className="pagebuilder-editor-fields">
          <TextFormatButtons />
          <div className="pagebuilder-sublist-header">
            <span className="pagebuilder-sublist-title">Badges ({items.length})</span>
            <button type="button" className="pagebuilder-add-btn pagebuilder-add-btn--sm" onClick={addItem}>
              <i className="bi bi-plus-lg" aria-hidden="true" /> Add Badge
            </button>
          </div>
          {items.length === 0 && <p className="pagebuilder-sublist-empty">No badges yet.</p>}
          {items.map((item, idx) => (
            <div key={item.id} className="pagebuilder-sublist-item pagebuilder-sublist-item--row">
              <Form.Control
                style={{ flex: 1 }}
                value={item.text}
                onChange={(e) => updateItem(item.id, 'text', e.target.value)}
                placeholder={`Badge ${idx + 1} label…`}
              />
              <div style={{ flex: '0 0 280px' }}>
                <IconClassPicker
                  value={item.icon || ''}
                  label=""
                  placeholder="Search badge icon..."
                  maxRows={2}
                  onChange={(nextIconClass) => updateItem(item.id, 'icon', nextIconClass)}
                />
              </div>
              <Form.Select
                style={{ flex: '0 0 140px' }}
                value={item.variant}
                onChange={(e) => updateItem(item.id, 'variant', e.target.value)}
              >
                <option value="primary">Primary</option>
                <option value="secondary">Secondary</option>
                <option value="success">Success</option>
                <option value="warning">Warning</option>
                <option value="danger">Danger</option>
                <option value="info">Info</option>
              </Form.Select>
              <button type="button" className="pagebuilder-block-action-btn pagebuilder-block-action-btn--danger" onClick={() => removeItem(item.id)} title="Remove">
                <i className="bi bi-trash" />
              </button>
            </div>
          ))}
        </div>
      );
    }

    default:
      return null;
  }
}
